// src/app/core/logging/global-error-handler.ts
import type { ErrorHandler} from '@angular/core';
import { Injectable, inject, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { LoggerService } from './logger.service';

/**
 * GlobalErrorHandler — captura todos los errores no manejados.
 *
 * Captura:
 * - Errores de componentes, pipes y guards de Angular
 * - Promise rejections no manejadas (via window.onunhandledrejection en main.ts)
 * - Errores RxJS no capturados por los interceptors
 *
 * NO propaga stack traces al DOM.
 * NO muestra mensajes técnicos al usuario.
 * NO hace logout en errores de componentes (podría perder contexto del usuario).
 */
@Injectable()
export class GlobalErrorHandler implements ErrorHandler {
  private readonly logger = inject(LoggerService);
  private readonly zone   = inject(NgZone);
  private readonly router = inject(Router);

  handleError(error: unknown): void {
    const message = this.extractMessage(error);

    // Registrar en el LoggerService (POST en producción, console.error en dev)
    this.logger.error('GlobalErrorHandler', error, {
      url: this.router.url,
    });

    // Angular lanza errores fuera de la zona NgZone.
    // Para navegar o actualizar el DOM, necesitamos volver a entrar.
    this.zone.run(() => {
      // ChunkLoadError: el usuario recargó la página o la red falló
      // al cargar un bundle lazy. No redirigir — el usuario puede recargar.
      if (message.includes('ChunkLoadError') || message.includes('Loading chunk')) {
        // El banner offline del AppComponent ya habrá aparecido si hay red caída
        return;
      }

      // Para otros errores críticos no manejados, loggear sin redirigir.
      // El componente que lanzó el error mostrará su propio estado de error.
      // Si el componente no tiene estado de error, el GlobalErrorHandler
      // es la última línea de defensa: no hacer nada visible es mejor que
      // redirigir al usuario inesperadamente.
    });
  }

  private extractMessage(error: unknown): string {
    if (error instanceof Error) {
      return error.message;
    }
    if (typeof error === 'string') {
      return error;
    }
    return 'Error desconocido';
  }
}
